import {Kind} from 'shared/constants';
import type {SimpleGridFieldValue} from 'src/features/record-grid/lib/types';
import {exists, isNullOrUndefined} from 'shared/utils/custom-utils';
import {
    hasTargetRecordReference,
    isErrorGridFieldValue,
    isOptionGridFieldValue
} from 'src/features/record-grid/lib/types-utils';
import type {LocalizeFunction} from 'core/types';
import {DateTime} from 'luxon';
import {datetimeEquals} from 'shared/utils/date-utils';
import type {
    EditorValueToGridFieldValueConversionParams
} from 'src/features/record-grid/components/cells/data-cell/types';
import type {SupportedEditors} from 'src/features/record-grid/lib/grid-value-editors';

export function convertGridFieldValueToEditorModel(fieldValue: SimpleGridFieldValue, editorType: SupportedEditors): any {
    if (isNullOrUndefined(fieldValue)) {
        return null;
    }

    if (isErrorGridFieldValue(fieldValue)) {
        return null;
    }

    if (hasTargetRecordReference(fieldValue) && isRecordEditor(editorType)) {
        return fieldValue.targetRecordReference;
    }

    if (isOptionGridFieldValue(fieldValue)) {
        return fieldValue;
    }

    return fieldValue.value;
}

export function convertEditorModelToGridFieldValue({
                                                       editorType,
                                                       currentFieldValue,
                                                       editorModelValue
                                                   }: EditorValueToGridFieldValueConversionParams, localize: LocalizeFunction): SimpleGridFieldValue {
    if (!exists(editorModelValue)) {
        return null;
    }

    if (editorType === 'text' || editorType === 'autocomplete' || editorType === 'markdown') {
        return {
            value: editorModelValue,
            text: editorModelValue
        };
    }

    if (editorType === 'date') {
        const updatedDateTime = DateTime.fromISO(editorModelValue);

        // custom equality check because different ISO string can represent equal datetime
        if (isNullOrUndefined(currentFieldValue) || !datetimeEquals(updatedDateTime, currentFieldValue.value)) {
            return {
                value: editorModelValue,
                text: updatedDateTime.toFormat('d.L.y')
            };
        }

        return currentFieldValue;
    }

    if (editorType === 'number') {
        return {
            value: editorModelValue,
            text: editorModelValue.toString(10)
        };
    }

    if (editorType === 'boolean') {
        return {
            value: editorModelValue,
            text: editorModelValue
                ? localize(/* @kp-localization commons.ANO */ 'commons.ANO')
                : localize(/* @kp-localization commons.NE */ 'commons.NE')
        };
    }

    if (editorType === 'single-acceptable') {
        return {
            text: editorModelValue.text,
            id: editorModelValue.id
        };
    }

    if (editorType === 'editable-record-search' || editorType === 'inline-record-search') {
        return {
            text: editorModelValue.text,
            value: editorModelValue.text,
            targetRecordReference: {
                id: editorModelValue.id,
                kind: Kind.KIND_RECORD,
                fond: editorModelValue.fond,
                text: editorModelValue.text
            }
        };
    }

    if (editorType === 'date-range') {
        return {
            value: editorModelValue,
            text: editorModelValue
        };
    }

    throw new Error(`Unsupported editor: ${editorType}`);
}

function isRecordEditor(editorType: SupportedEditors): boolean {
    return ['inline-record-search', 'editable-record-search'].includes(editorType);
}